import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch/QuickSearch';
import NavigationContainer from './Containers/Navigation.container';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import FooterMenus from './Components/FooterMenus';
import NewsletterOptin from './Components/NewsletterOptin';
import './Containers/Vanilla.container';
import StickyCta from './Components/StickyCta';
import ProductSpecificationArea from './Components/ProductSpecificationArea';
import VariantPickerContainer from './Components/ProductPage/VariantPickerContainer';
import MobileStickyBuyButton from './Components/ProductPage/MobileStickyBuyButton';
import FooterUspSlider from './Components/FooterUspSlider';
import FaqItems from './Components/FaqItems';
import QuickSearchContainer from './Containers/QuickSearch.container';
import CustomVariantContainer from './Components/ProductPage/CustomVariantContainer';
import MiniCartContainer from './Containers/MiniCart.container';
import NewsMasonry from './Components/NewsMasonry';
import CategoryProductsView from './Components/CategoryProductsView';
import CategoryProductsListView from './Components/CategoryProductsListView';
import './Server/Redux.server';
import QuickOrderContainer from './Containers/QuickOrderContainer';
import ActiveFilterBar from './Components/ActiveFilterBar';
import ChannelPickerTrigger from './Components/ChannelPickerTrigger';

ReduxServer.createStore(global.LitiumPreloadState);

const hydrateServerComponents = () => {
  import('./Server/expose-components').then(() => {
    const reactLoadedEvent = new Event('ReactLoaded');
    window.dispatchEvent(reactLoadedEvent);
  });
};

hydrateServerComponents();

// Add a dictionary to keep track of the registered async reducers
global.LitiumReduxStore.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
global.LitiumReduxStore.injectReducer = (key, asyncReducer) => {
  if (!global.LitiumReduxStore.asyncReducers[key]) {
    global.LitiumReduxStore.asyncReducers[key] = asyncReducer;
    global.LitiumReduxStore.replaceReducer(
      createReducer(global.LitiumReduxStore.asyncReducers)
    );
  }
};

window.__litium = {
  ...window.__litium,
  bootstrapComponents: () => {
    // bootstrap React components, in case the HTML response we receive from the server
    // has React components. ReactDOM.render performs only an update on previous rendered
    // components and only mutate the DOM as necessary to reflect latest React element.
    bootstrapComponents();
  },
  cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(element, container, callback);
};

window.onunload = () => {
  // make sure components are unmounted, redux's listener are unsubscribed
  // to fix issue with iframe navigation in IE
  registeredContainers.forEach((container) => {
    ReactDOM.unmountComponentAtNode(container);
  });
};

const bootstrapComponents = () => {
  const cartElem = document.getElementById('miniCart');
  if (cartElem) {
    const paymentLogoElem = cartElem.querySelector('json-object');
    const paymentLogos = paymentLogoElem
      ? JSON.parse(paymentLogoElem.innerHTML)
      : [];
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <MiniCartContainer paymentLogos={paymentLogos} />
      </Provider>,
      cartElem
    );
  }
  const quickSearchElems = document.getElementsByClassName(
    'quick-search-container'
  );
  if (quickSearchElems?.length > 0) {
    for (let elem of quickSearchElems) {
      const dataCase = elem.dataset['case'];
      const menuElem = elem.querySelector('#quick-search-menu');
      const dataMenus = menuElem ? JSON.parse(menuElem.innerText) : {};
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <QuickSearchContainer dataCase={dataCase} dataMenus={dataMenus} />
        </Provider>,
        elem
      );
    }
  }
  if (document.getElementById('facetedSearch')) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <FacetedSearch />
      </Provider>,
      document.getElementById('facetedSearch')
    );
  }
  if (document.getElementById('facetedSearchCompact')) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <FacetedSearchCompactContainer />
      </Provider>,
      document.getElementById('facetedSearchCompact')
    );
  }
  if (document.getElementById('myPagePersons')) {
    const PersonList = DynamicComponent({
      loader: () => import('./Components/PersonListContainer'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <PersonList />
      </Provider>,
      document.getElementById('myPagePersons')
    );
  }
  if (document.getElementById('myPageAddresses')) {
    const AddressList = DynamicComponent({
      loader: () => import('./Components/AddressListContainer'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <AddressList />
      </Provider>,
      document.getElementById('myPageAddresses')
    );
  }
  if (document.getElementById('checkout')) {
    const checkoutDeliveryMessage = document.getElementById('checkout').dataset[
      'checkoutDeliveryMessage'
    ];
    const Checkout = DynamicComponent({
      loader: () => import('./Components/Checkout'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <Checkout checkoutDeliveryMessage={checkoutDeliveryMessage} />
      </Provider>,
      document.getElementById('checkout')
    );
  }
  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Components/LightboxImages'),
    });
    import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
      global.LitiumReduxStore.injectReducer('lightboxImages', lightboxImages);
      const rootElement = document.getElementById('lightBoxImages');
      const images = Array.from(
        rootElement.querySelectorAll('a')
      ).map((img) => ({ src: img.dataset.src }));
      const thumbnails = Array.from(
        rootElement.querySelectorAll('a img')
      ).map((img) => ({ src: img.src }));
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <LightboxImages images={images} thumbnails={thumbnails} />
        </Provider>,
        document.getElementById('lightBoxImages')
      );
    });
  }

  if (document.querySelectorAll('#channel-picker-container')) {
    const ChannelPicker = DynamicComponent({
      loader: () => import('./Components/ChannelPickerTrigger'),
    });
    Array.from(document.querySelectorAll('#channel-picker-container')).forEach(
      (picker) => {
        try {
          renderReact(
            <Provider store={global.LitiumReduxStore}>
              <ChannelPickerTrigger
                items={JSON.parse(picker.querySelector('json-data').innerText)}
              />
            </Provider>,
            picker
          );
        } catch (error) {
          console.error('Error parsing JSON data:', error);
        }
      }
    );
  }

  if (document.querySelectorAll('.slider').length > 0) {
    const Slider = DynamicComponent({
      loader: () => import('./Components/Slider'),
    });
    Array.from(document.querySelectorAll('.slider')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('.slider__block')].map(
          (block) => {
            return {
              image: block.dataset.image,
              url: block.dataset.url,
              text: block.dataset.text,
              actionText: block.dataset.actionText,
            };
          }
        );
        if (values.length > 0) {
          renderReact(<Slider values={values} />, slider);
        }
      }
    );
  }

  if (document.querySelectorAll('buy-button').length > 0) {
    const BuyButton = DynamicComponent({
      loader: () => import('./Components/BuyButton'),
    });
    Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
      const {
        articleNumber,
        quantityFieldId,
        href,
        cssClass,
        label,
      } = button.dataset;
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <BuyButton
            {...{
              label,
              articleNumber,
              quantityFieldId,
              href,
              cssClass,
            }}
          />
        </Provider>,
        button
      );
    });
  }
  const googleMap = document.getElementById('google-maps-react-root');
  if (googleMap) {
    const GoogleMapContainer = DynamicComponent({
      loader: () => import('./Containers/GoogleMapContainer'),
    });
    const { markers, lang, key } = googleMap.dataset;

    ReactDOM.render(
      <Provider store={global.LitiumReduxStore}>
        <GoogleMapContainer
          language={lang}
          markers={JSON.parse(markers)}
          apiKey={key}
        />
      </Provider>,
      googleMap
    );
  }

  const storePageMap = document.getElementById('google-maps-react-page');
  if (storePageMap) {
    const GoogleMapContainer = DynamicComponent({
      loader: () => import('./Containers/StorePageMapContainer'),
    });
    const { marker, lang, key } = storePageMap.dataset;

    ReactDOM.render(
      <Provider store={global.LitiumReduxStore}>
        <GoogleMapContainer
          language={lang}
          marker={JSON.parse(marker)}
          apiKey={key}
        />
      </Provider>,
      storePageMap
    );
  }

  const newsItem = document.getElementById('masonry-page');
  if (newsItem) {
    const { items } = newsItem.dataset;
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <NewsMasonry items={JSON.parse(items)} />
      </Provider>,
      newsItem
    );
  }

  const standardNewsItem = document.getElementById('standard__news-page');
  if (standardNewsItem) {
    const StandardNews = DynamicComponent({
      loader: () => import('./Components/NewsStandard'),
    });
    const { items } = standardNewsItem.dataset;
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <StandardNews items={JSON.parse(items)} />
      </Provider>,
      standardNewsItem
    );
  }

  const productImageSliderElem = document.getElementById(
    'product-image-slider'
  );
  if (productImageSliderElem) {
    const ProductImageSlider = DynamicComponent({
      loader: () => import('./Components/ProductImageSlider'),
    });
    const images = Array.from(
      productImageSliderElem.querySelectorAll('img')
    ).map((img) => ({
      src: img.src,
      alt: img.alt,
      hdSrc: img.dataset.hdImage,
      originalSrc: img.dataset.originalImage,
      thumbnailSrc: img.dataset.thumbnailImage,
    }));
    let otherProductTagData = productImageSliderElem.querySelector('json-data')
      .innerText;
    if (otherProductTagData) {
      otherProductTagData = JSON.parse(otherProductTagData);
    }
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ProductImageSlider
          images={images}
          minHeight={productImageSliderElem.dataset.minHeight}
          hasConfigurator={
            productImageSliderElem.dataset.hasConfigurator == 'True'
          }
          otherProductTags={otherProductTagData}
        />
      </Provider>,
      productImageSliderElem
    );
  }

  const footerMenus = document.getElementById('footerMenus');
  if (footerMenus) {
    const menuItems = JSON.parse(
      footerMenus.querySelector('json-data').innerText
    );
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <FooterMenus menuItems={menuItems} />
      </Provider>,
      footerMenus
    );
  }

  const footerNewsLetter = document.getElementById('footerNewsLetter');
  if (footerNewsLetter) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <NewsletterOptin />
      </Provider>,
      footerNewsLetter
    );
  }
  const productSpecificationArea = document.getElementById(
    'product-page-specification-area'
  );
  if (productSpecificationArea) {
    const productSpecifications = JSON.parse(
      productSpecificationArea.querySelector(
        'json-data[data-id="product-specification"]'
      ).innerText
    );
    const isB2b = productSpecificationArea.dataset['isB2b'] == 'True';
    const showProductShippingInformation = JSON.parse(
      productSpecificationArea.querySelector(
        'json-data[data-id="show-product-shippinginformation"]'
      ).innerHTML
    );
    const productDescription = productSpecificationArea.querySelector(
      'json-data[data-id="product-description"]'
    )?.innerHTML;
    const productShippingTemplate = JSON.parse(
      productSpecificationArea.querySelector(
        'json-data[data-id="product-shipping-template"]'
      ).innerHTML
    );
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ProductSpecificationArea
          productSpecifications={productSpecifications}
          productDescription={productDescription}
          productShippingTemplate={productShippingTemplate}
          isB2b={isB2b}
          showProductShippingInformation={showProductShippingInformation}
        />
      </Provider>,
      productSpecificationArea
    );
  }
  const productVariantPickerElement = document.getElementById(
    'product-variant-picker'
  );
  if (productVariantPickerElement) {
    const isBuyableChannel =
      productVariantPickerElement.dataset['isBuyableChannel'] == 'True';
    const storesUrl = productVariantPickerElement.dataset['storesUrl'];
    const hasSelectedVariant =
      productVariantPickerElement.dataset['hasSelectedVariant'];
    const selectedVariantId =
      productVariantPickerElement.dataset['selectedVariantId'];
    const productVariants = JSON.parse(
      productVariantPickerElement.querySelector(
        'json-data[data-id="product-variants"]'
      ).innerText
    );
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <VariantPickerContainer
          productVariants={productVariants}
          isBuyableChannel={isBuyableChannel}
          storesUrl={storesUrl}
          hasSelectedVariant={hasSelectedVariant}
          selectedVariantId={selectedVariantId}
        />
      </Provider>,
      productVariantPickerElement
    );
  }
  let overlayContainerElem = document.getElementById('overlayContainer');
  if (overlayContainerElem) {
    const OverlayContainer = DynamicComponent({
      loader: () => import('./Containers/Overlay.container'),
    });

    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <OverlayContainer />
      </Provider>,
      overlayContainerElem
    );
  }
  const customVariantElem = document.getElementById('product-custom-variant');
  if (customVariantElem) {
    const isBuyableChannel =
      customVariantElem.dataset['isBuyableChannel'] == 'True';
    const isB2b = customVariantElem.dataset['isB2b'] == 'True';
    const storesUrl = customVariantElem.dataset['storesUrl'];
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <CustomVariantContainer
          configurations={JSON.parse(
            customVariantElem.querySelector('json-data').innerText
          )}
          isBuyableChannel={isBuyableChannel}
          isB2b={isB2b}
          storesUrl={storesUrl}
        />
      </Provider>,
      customVariantElem
    );
  }
  const navigationMobileTrigger = document.getElementById(
    'navigation-mobile-trigger'
  );
  if (navigationMobileTrigger) {
    const NavigationMobileTrigger = DynamicComponent({
      loader: () => import('./Components/NavigationMobileTrigger'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <NavigationMobileTrigger element={navigationMobileTrigger} />
      </Provider>,
      navigationMobileTrigger
    );
  }
  const productSliderBlockElems = document.getElementsByClassName(
    'product-slider-block-container'
  );
  if (productSliderBlockElems?.length > 0) {
    const ProductSliderBlock = DynamicComponent({
      loader: () => import('./Components/ProductSliderBlock'),
    });
    for (let elem of productSliderBlockElems) {
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <ProductSliderBlock productCardElements={Array.from(elem.children)} />
        </Provider>,
        elem
      );
    }
  }
  const faqItemsElem = document.querySelectorAll('.faq-block__container');
  faqItemsElem.forEach((faqItem) => {
    const faqItemsData = JSON.parse(
      faqItem.querySelector('json-data').innerText
    );
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <FaqItems faqItemsData={faqItemsData} />
      </Provider>,
      faqItem
    );
  });
  const footerUspElem = document.getElementById('footer-usp-container');
  if (footerUspElem) {
    const uspItems = JSON.parse(
      footerUspElem.querySelector('json-data').innerText
    );
    if (footerUspElem) {
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <FooterUspSlider uspItems={uspItems} />
        </Provider>,
        footerUspElem
      );
    }
  }
  if (document.getElementById('my-pages-navigation')) {
    const MyPagesNavigation = DynamicComponent({
      loader: () => import('./Components/MyPages/MyPagesNavigation'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <MyPagesNavigation />
      </Provider>,
      document.getElementById('my-pages-navigation')
    );
  }
  if (document.getElementById('my-pages-tab-content')) {
    const MyPagesTabContainer = DynamicComponent({
      loader: () => import('./Components/MyPages/MyPagesTabContainer'),
    });

    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <MyPagesTabContainer />
      </Provider>,
      document.getElementById('my-pages-tab-content')
    );
  }
  const productListViewElem = document.getElementById(
    'product-list-listview-prerender'
  );
  if (productListViewElem) {
    const products = JSON.parse(productListViewElem.innerHTML);
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <CategoryProductsListView products={products} />
      </Provider>,
      productListViewElem
    );
  }
  const productListCardsElem = document.getElementById(
    'product-list-cards-prerender'
  );
  if (productListCardsElem) {
    const products = JSON.parse(productListCardsElem.innerHTML);
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <CategoryProductsView products={products} />
      </Provider>,
      productListCardsElem
    );
  }
  const quickOrderElem = document.getElementById('quick-order-container');
  if (quickOrderElem) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <QuickOrderContainer />
      </Provider>,
      quickOrderElem
    );
  }
  const priceHistoryElems = document.querySelectorAll('#price-history');
  if (!!priceHistoryElems.length) {
    const PriceHistoryContainer = DynamicComponent({
      loader: () => import('./Containers/PriceHistoryContainer'),
    });

    for (let elem of priceHistoryElems) {
      const priceHistory = {
        description: elem.dataset.description,
        numberOfDays: elem.dataset.numberofdays,
        previouslyLowestPrice: elem.dataset.previouslylowestprice,
        title: elem.dataset.title,
        id: elem.dataset.id,
      };

      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <PriceHistoryContainer priceHistory={priceHistory} />
        </Provider>,
        elem
      );
    }
  }

  const priceToggleElems = document.getElementsByClassName('price-toggle');
  if (priceToggleElems?.length > 0) {
    const PriceToggle = DynamicComponent({
      loader: () => import('./Components/PriceToggle'),
    });
    for (let elem of priceToggleElems) {
      renderReact(
        <Provider store={global.LitiumReduxStore}>
          <PriceToggle />
        </Provider>,
        elem
      );
    }
  }

  const activeFilterBarElem = document.getElementById('active-filter-bar');
  if (activeFilterBarElem) {
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <ActiveFilterBar />
      </Provider>,
      activeFilterBarElem
    );
  }

  const categorySortingBar = document.getElementById('category-sorting-bar');
  if (categorySortingBar) {
    const CategorySortingBar = DynamicComponent({
      loader: () => import('./Components/CategorySortingBar'),
    });
    renderReact(
      <Provider store={global.LitiumReduxStore}>
        <CategorySortingBar />
      </Provider>,
      categorySortingBar
    );
  }
};

bootstrapComponents();
